import React, { useEffect, useLayoutEffect, useState } from "react";
import { Button, Card, TextField } from "@vitalsource/vstui";
import { createThemedComponent } from "@vitalsource/vstui/themes";
import { Group, XSmall } from "@vitalsource/vstui-icons";
import { useIntl } from "react-intl";
import styled from "styled-components";
import withTextSelect, { MenuItemType, PopoverProps } from "./withTextSelect";
import ReactDOM from "react-dom";
import { cloud } from "@vitalsource/vstui/colors";
import { copyTextToClipboard } from "../../utils/copy";
import { SmallLoader } from "./../common/loading/DotLoader";
import { showToast } from "../common/Toast";
import { ToastType } from "../common/Toast/ToastContent";
import useBrandProp from "components/brand-context/useBrandProp";

const EMPTY_STRING = "";
const WINDOW_PADDING = 20;
const MENU_PADDING = 12;
const MENU_ITEM_INSET = 3; // Keep the focus rect from being cut off

const MenuButton = createThemedComponent(Button, {
  Button_paddingHorizontal_fullWidth: `${MENU_PADDING - MENU_ITEM_INSET}px`,
  ButtonContent_justifyContent: "left",
  Button_fontWeight_naked: 400,
});
const Styled = {
  GroupsIcons: styled(Group)<{ stroke: string }>`
    transform: scale(0.65);
    color: ${p => p.stroke};
  `,
  MenuContainer: styled(Card)<{ top: number; left: number }>`
    width: 250px;
    z-index: 99;
    position: absolute;
    top: ${p => p.top}px;
    left: ${p => p.left}px;
  `,
  XSmallIcon: styled(XSmall)`
    transform: scale(0.75);
    color: #fff;
  `,
  ColorButton: styled.button<ColorProps>`
    &:nth-child(${p => p.colorsPerRow}n) {
      margin-right: 0px;
    }
    align-items: center;
    background: ${p => p.background};
    border-radius: 100%;
    border: ${p => (p.active ? `3px` : `1px`)} solid;
    border-color: ${p => p && p.border};
    display: flex;
    justify-content: center;
    height: 25px;
    margin-bottom: 8px;
    margin-right: calc(
      (224px - (25px * ${p => p.colorsPerRow})) / ${p => p.colorsPerRow - 1}
    );
    width: 25px;
  `,
  XContainer: styled.div`
    min-width: 25px;
    min-height: 25px;
    background: rgba(0, 0, 0, 0.3);
    border-radius: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  ColorPallete: styled.div`
    flex-direction: row;
    display: flex;
    flex-wrap: wrap;
    padding: 0 ${MENU_PADDING}px 15px ${MENU_PADDING}px;
  `,
  MarkerName: styled.div`
    width: 100%;
    margin: 8px 0;
    min-height: 15px;
    color: ${cloud.getShade(7)};
    font-size: 1.1rem;
    font-weight: 500;
    line-height: 13px;
    padding: 0 ${MENU_PADDING}px;
  `,
  TextFieldContainer: styled.div`
    padding: 0 ${MENU_PADDING}px;
    margin-bottom: -20px;
  `,
  MenuItemsContainer: styled.div`
    padding: 8px ${MENU_ITEM_INSET}px;
  `,
};

interface ColorProps {
  border: string;
  background: string;
  active: boolean;
  colorsPerRow: number;
}

export const HighlightPopover = ({
  open,
  loading,
  coords,
  noteText,
  setNoteText,
  clickedMarkerId,
  activeMarker,
  setActiveMarker,
  highlighters = [],
  selectedText,
  updateHighlight,
  deleteHighlight,
  createHighlight,
  closeMenu,
}: PopoverProps) => {
  const [marker, setMarkerLabel] = useState(EMPTY_STRING);
  const [yCoords, setYCoords] = useState(0);
  const [xCoords, setXCoords] = useState(0);
  const menuRef = React.useRef(null);
  const node = ReactDOM.findDOMNode(menuRef.current) as any;
  const menuHeight = node && node.offsetHeight;
  const menuWidth = node && node.offsetWidth;
  const intl = useIntl();
  const notesEnabled = useBrandProp("Highlights.Notes.enabled");

  const menuItems = [
    {
      enabled: useBrandProp("Copy.enabled"),
      label: intl.formatMessage({ id: "general.copy" }),
      onClick: () => {
        if (selectedText) {
          copyTextToClipboard(selectedText);
          const messageId = clickedMarkerId
            ? "highlight.highlightCopy"
            : "highlight.selectionCopy";
          showToast(ToastType.SUCCESS, intl.formatMessage({ id: messageId }));
          closeMenu();
        }
      },
    },
  ];

  const setYLocation = () => {
    // low, high, default
    const popoverRef = getMenuElement();
    if (popoverRef && popoverRef.parentElement) {
      const areaHeight = popoverRef.parentElement.offsetHeight;
      if (coords.y + menuHeight > areaHeight) {
        if (menuHeight > areaHeight) {
          setYCoords(0);
        } else {
          setYCoords(areaHeight - menuHeight - WINDOW_PADDING);
        }
      } else if (coords.y < 0) {
        setYCoords(WINDOW_PADDING);
      } else {
        setYCoords(coords.y + WINDOW_PADDING);
      }
    }
  };

  const setXLocation = () => {
    const popoverRef = getMenuElement();
    if (popoverRef && popoverRef.parentElement) {
      const areaWidth = popoverRef.parentElement.offsetWidth;
      // right, left, default
      if (coords.x + menuWidth > areaWidth) {
        setXCoords(areaWidth - menuWidth - WINDOW_PADDING);
      } else if (coords.x < 0) {
        setXCoords(coords.x + WINDOW_PADDING);
      } else {
        setXCoords(coords.x);
      }
    }
  };

  const getMenuElement = () => {
    return ReactDOM.findDOMNode(menuRef.current);
  };

  useLayoutEffect(() => {
    if (menuRef.current) {
      setYLocation();
      setXLocation();
    }
  });

  useEffect(() => {
    activeMarker && setMarkerLabel(activeMarker.label);
  }, [activeMarker]);

  const colorClick = (c: jigsaw.Marker) => {
    if (c.globalId === clickedMarkerId) {
      deleteHighlight(c);
    } else if (clickedMarkerId && c.globalId !== clickedMarkerId) {
      updateHighlight(c);
    } else {
      createHighlight(c);
      setActiveMarker(c);
    }
    closeMenu();
  };

  const renderIcon = (c: jigsaw.Marker, isForSelectedHighlight: boolean) => {
    let icon;
    if (c.shared) {
      icon = <Styled.GroupsIcons stroke={c.vstColorDark} />;
    }
    if (isForSelectedHighlight) {
      icon = (
        <Styled.XContainer>
          <Styled.XSmallIcon />
        </Styled.XContainer>
      );
    }
    return icon;
  };

  const renderTextField = () => {
    if (notesEnabled) {
      return loading ? (
        <SmallLoader />
      ) : (
        <Styled.TextFieldContainer>
          <TextField
            multiline
            rows={2}
            label={intl.formatMessage({ id: "highlight.noteText" })}
            placeholder={intl.formatMessage({ id: "highlight.addNote" })}
            onChange={e => setNoteText(e.currentTarget.value)}
            type="text"
            value={noteText}
          />
        </Styled.TextFieldContainer>
      );
    }
  };

  if (open) {
    return (
      <Styled.MenuContainer innerRef={menuRef} top={yCoords} left={xCoords}>
        <Styled.MarkerName>{marker}</Styled.MarkerName>
        <Styled.ColorPallete>
          {highlighters.map((c: jigsaw.Marker) => {
            const active =
              !!activeMarker && activeMarker.globalId === c.globalId;
            const isForSelectedHighlight = c.globalId === clickedMarkerId;
            return (
              <Styled.ColorButton
                colorsPerRow={6}
                active={active}
                key={c.globalId}
                aria-label={
                  isForSelectedHighlight
                    ? intl.formatMessage({ id: "highlight.deleteHighlight" })
                    : c.label
                }
                background={c.vstColor}
                border={c.vstColorDark}
                onMouseEnter={() => setMarkerLabel(c.label)}
                onMouseLeave={() =>
                  setMarkerLabel(
                    (activeMarker && activeMarker.label) || EMPTY_STRING
                  )
                }
                onClick={() => colorClick(c)}
              >
                {renderIcon(c, isForSelectedHighlight)}
              </Styled.ColorButton>
            );
          })}
        </Styled.ColorPallete>
        {renderTextField()}
        <Styled.MenuItemsContainer>
          {menuItems.map((item: MenuItemType) => {
            return (
              item.enabled && (
                <div key={item.label}>
                  <MenuButton
                    onClick={item.onClick}
                    fullWidth
                    naked
                    color="neutral"
                  >
                    {item.label}
                  </MenuButton>
                </div>
              )
            );
          })}
        </Styled.MenuItemsContainer>
      </Styled.MenuContainer>
    );
  }
  return null;
};

export const PopoverWithSelect = withTextSelect(HighlightPopover);
